// Selectors over the run-store for async continuations. Each takes a `RunState` snapshot: a
// continuation passes `getRunState()` (the live store) and the id it captured when its work
// started, never the render closure's `active`/`runState` — the store reflects every dispatch
// at once, where a render-time mirror only catches up on the next render. Pure: no React, DOM
// or network.
//
// The run-tree selectors (`selectRun`, `selectBranchRuns`, …) stay in `lib/forecast/selectors.ts`;
// this module holds the conversation-slice reads.
import { DRAFT_CONV_ID } from "../lib/forecast/draft.js";
import type { Entry } from "../lib/forecast/model.js";
import type { RunState } from "../lib/forecast/reducer.js";

const NO_ENTRIES: readonly Entry[] = Object.freeze([]);

/**
 * The entries of conversation `convId` (a run's `conversationId`, or its `runId` for a run whose
 * thread is keyed by it), or an empty list when there is no such conversation or no id.
 */
export function selectConversationEntries(
  state: RunState | null | undefined,
  convId: string | null | undefined,
): readonly Entry[] {
  if (!convId) return NO_ENTRIES;
  return state?.conversationsById?.[convId]?.entries ?? NO_ENTRIES;
}

/** The draft conversation's entries (the chat before any forecast exists), or an empty list. */
export function selectDraftEntries(state: RunState | null | undefined): readonly Entry[] {
  return selectConversationEntries(state, DRAFT_CONV_ID);
}
